import { getGlobalLogger } from '@mntn/logger'
import { trace } from '@mntn/observability'
import { toOtelAttributes } from '@mntn/observability/core'
import _snakeCase from 'lodash.snakecase'
import { isPiiKey, sanitizeContext } from './sanitize-context.ts'

export type Result<TData, TError = Error> = { ok: true; data: TData } | { ok: false; error: TError }

// Safe to capture at module scope: the wrapper resolves the registered instance at call time,
// so an app that registers inside main() — after this module loads — is still heard.
const logger = getGlobalLogger()

function snakeCase(str: string) {
  return str.split('.').map(_snakeCase).join('.')
}

/** Span attributes are `app.`-prefixed so they cannot collide with OTel semantic conventions. */
const toAppSpanAttributes = (spanAttributes: Record<string, string | number | boolean>) => {
  const result: Record<string, string | number | boolean> = {}

  for (const [key, value] of Object.entries(spanAttributes)) {
    if (isPiiKey(key)) continue
    result[`app.${snakeCase(key)}`] = value
  }

  return result
}

/**
 * Creates a failure result, records the error's identity on the active span, and logs the
 * context.
 *
 * The span always receives the error's identity: a fixed, collision-free set. A caller may also
 * name scalar `spanAttributes` to record on the span — `app.`-prefixed and PII-filtered, like
 * `success` — for dimensions worth querying a failure by. The rest of the context and
 * `error.payload` go to the log line, which the logger correlates to this span by `trace_id`.
 *
 * @example
 * return failure({
 *   customCode: 'OFFERING_NOT_FOUND_ERROR',
 *   message: 'Not found',
 * })
 * // span attributes:
 * //   error.type: "OFFERING_NOT_FOUND_ERROR"
 * //   error.message: "Not found"
 * //   error.code: "OFFERING_NOT_FOUND_ERROR"
 * // log (error): result.failure { errorName, errorMessage, errorCode }
 *
 * @example
 * return failure(
 *   { customCode: 'OFFERING_NOT_FOUND_ERROR', message: 'Not found' },
 *   { offeringId: 123 },
 * )
 * // span attributes: error identity only — plain context never reaches the span
 * // log (error): result.failure { errorName, errorMessage, errorCode, context: { offeringId: 123 } }
 *
 * @example
 * return failure({ customCode: 'TENANT_ACCESS_DENIED_ERROR', message: 'Denied' }, {
 *   spanAttributes: { 'requested.tenant.advertiserId': 7 },
 * })
 * // span attributes: error identity + app.requested.tenant.advertiser_id: 7
 */
type FailureContext = {
  spanAttributes?: Record<string, string | number | boolean>
} & Record<string, unknown>

type LoggableValue = number | boolean | string | undefined | { toString(): string }
type ErrorPayload = Record<string, LoggableValue | Record<string, LoggableValue>>

type StructuralError<CustomCode extends string = string, Payload extends ErrorPayload = ErrorPayload> = Readonly<{
  name: string
  message: string
  customCode: CustomCode
  payload?: Payload
  cause?: unknown
  critical?: boolean
  stack?: string
  toJSON: () => unknown
}>

type NormalizedError<CustomCode extends string = string, Payload extends ErrorPayload = ErrorPayload> = Readonly<{
  name: string
  message: string
  customCode: CustomCode
  payload?: Payload
  cause?: Error | StructuralError
  critical?: boolean
  stack?: string
  toJSON: () => {
    name: string
    message: string
    customCode: CustomCode
    payload: Payload | undefined
    stack: string | undefined
    cause: Error | StructuralError | undefined
  }
}>

type InlineErrorInput<CustomCode extends string, Payload extends ErrorPayload> = Readonly<{
  customCode: CustomCode
  name?: string
  message: string
  payload?: Payload
  cause?: unknown
  critical?: boolean
  stack?: never
  toJSON?: never
}>

function isInlineError<Value>(value: Value): value is Value & InlineErrorInput<string, ErrorPayload> {
  if (value === null || typeof value !== 'object' || value instanceof Error) {
    return false
  }

  const customCode = Object.getOwnPropertyDescriptor(value, 'customCode')?.value
  const message = Object.getOwnPropertyDescriptor(value, 'message')?.value
  const toJSON = Object.getOwnPropertyDescriptor(value, 'toJSON')?.value

  return typeof customCode === 'string' && typeof message === 'string' && toJSON === undefined
}

// biome-ignore lint/plugin: PropertyDescriptor.value is any; this boundary keeps it unknown until validated.
function getInheritedDataProperty(value: object, key: string): unknown {
  let prototype: object | null = Object.getPrototypeOf(value)

  while (prototype) {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, key)
    if (descriptor) return descriptor.value
    prototype = Object.getPrototypeOf(prototype)
  }
}

function isNormalizedError(value: unknown): value is StructuralError {
  if (value === null || typeof value !== 'object') return false

  const name = Object.getOwnPropertyDescriptor(value, 'name')?.value
  const customCode = Object.getOwnPropertyDescriptor(value, 'customCode')?.value
  const message = Object.getOwnPropertyDescriptor(value, 'message')?.value
  const toJSON = Object.getOwnPropertyDescriptor(value, 'toJSON')?.value ?? getInheritedDataProperty(value, 'toJSON')

  return (
    typeof name === 'string' &&
    typeof customCode === 'string' &&
    typeof message === 'string' &&
    typeof toJSON === 'function'
  )
}

function isRetainedCause(value: unknown): value is Error | StructuralError {
  return value instanceof Error || isNormalizedError(value)
}

function createInlineError<CustomCode extends string, Payload extends ErrorPayload>(
  input: InlineErrorInput<CustomCode, Payload>,
) {
  const name = input.name ?? input.customCode
  const cause = isRetainedCause(input.cause) ? input.cause : undefined

  return {
    name,
    message: input.message,
    customCode: input.customCode,
    ...(input.payload !== undefined && { payload: input.payload }),
    ...(cause !== undefined && { cause }),
    ...(input.critical !== undefined && {
      critical: input.critical,
    }),
    stack: undefined,
    toJSON: () => ({
      name,
      message: input.message,
      customCode: input.customCode,
      payload: input.payload,
      stack: undefined,
      cause,
    }),
  } satisfies NormalizedError<CustomCode, Payload>
}

export function toError<
  const Input extends InlineErrorInput<string, ErrorPayload> | StructuralError<string, ErrorPayload>,
>(input: Input) {
  const normalized = isInlineError(input) ? createInlineError(input) : input
  const cause = isRetainedCause(normalized.cause) ? normalized.cause : undefined
  const error = new Error(normalized.message)

  return Object.assign(error, {
    name: normalized.name,
    customCode: normalized.customCode,
    ...(normalized.payload !== undefined && { payload: normalized.payload }),
    ...(cause !== undefined && { cause }),
    ...(normalized.critical !== undefined && {
      critical: normalized.critical,
    }),
    toJSON: () => ({
      name: normalized.name,
      message: normalized.message,
      customCode: normalized.customCode,
      payload: normalized.payload,
      stack: error.stack,
      cause,
    }),
  })
}

function getFailure<const Input extends InlineErrorInput<string, ErrorPayload>>(value: Input) {
  const error = createInlineError(value)
  return {
    result: error,
    name: error.name,
    message: error.message,
    critical: error.critical === true,
    code: error.customCode,
    payload: error.payload,
  }
}

type NormalizedInlineError<Input> = Input extends {
  customCode: infer CustomCode extends string
}
  ? Input extends { payload: infer Payload extends ErrorPayload }
    ? NormalizedError<CustomCode, Payload>
    : NormalizedError<CustomCode, Record<never, never>>
  : never

export function failure<const Input extends InlineErrorInput<string, ErrorPayload>, Context extends FailureContext>(
  error: Input,
  context: Context,
): {
  ok: false
  error: NormalizedInlineError<Input>
  context: Context
}
export function failure<const Input extends InlineErrorInput<string, ErrorPayload>>(
  error: Input,
): { ok: false; error: NormalizedInlineError<Input> }
export function failure(
  error: InlineErrorInput<string, ErrorPayload>,
  context?: FailureContext,
): {
  ok: false
  error: NormalizedError<string, ErrorPayload>
  context?: FailureContext
} {
  const span = trace.getActiveSpan()
  const { result, name, message, critical, code, payload } = getFailure(error)

  if (span) {
    span.setAttributes(
      toOtelAttributes({
        'error.type': name,
        'error.message': message,
        ...(critical && { error: true }),
        ...(code && { 'error.code': code }),
      }),
    )
    if (context?.spanAttributes) {
      span.setAttributes(toAppSpanAttributes(context.spanAttributes))
    }
  }

  logger.error('result.failure', {
    errorName: name,
    errorMessage: message,
    ...(code && { errorCode: code }),
    ...(payload && { payload: sanitizeContext(payload) }),
    ...(context && { context: sanitizeContext(context) }),
  })

  return context === undefined ? { ok: false as const, error: result } : { ok: false as const, error: result, context }
}

/**
 * Creates a success result, optionally records named scalar attributes on the active span, and
 * logs the context.
 *
 * `spanAttributes` are the only values that reach the span: scalars, named by the caller,
 * `app.`-prefixed. Everything else in the context goes to the log line.
 *
 * @example
 * return success(user)
 * // no span write, no log
 *
 * @example
 * return success(user, { event: 'fetchUser', userId: 1 })
 * // log (debug): result.success { event: "fetch_user", userId: 1 }
 *
 * @example
 * return success(principal, {
 *   event: 'resolvePrincipal',
 *   spanAttributes: { tenantKind: 'advertiser', advertiserId: 7 },
 * })
 * // span attributes: app.tenant_kind: "advertiser", app.advertiser_id: 7
 * // log (debug): result.success { event: "resolve_principal", spanAttributes: { … } }
 */
type SuccessContext = {
  event?: string
  spanAttributes?: Record<string, string | number | boolean>
} & Record<string, unknown>

export function success<T, C extends SuccessContext>(data: T, context: C): { ok: true; data: T; context: C }
export function success<T>(data: T): { ok: true; data: T }
export function success(): { ok: true; data: undefined }
export function success<T, C extends SuccessContext>(data?: T, context?: C) {
  if (context) {
    const { event, spanAttributes, ...rest } = context

    if (spanAttributes) {
      trace.getActiveSpan()?.setAttributes(toAppSpanAttributes(spanAttributes))
    }

    logger.debug('result.success', {
      ...(event && { event: snakeCase(event) }),
      ...(spanAttributes && {
        spanAttributes: sanitizeContext(spanAttributes),
      }),
      ...sanitizeContext(rest),
    })
  }

  return context === undefined ? { ok: true as const, data } : { ok: true as const, data, context }
}
